import { Breadcrumbs, Link, Typography, Divider } from "@mui/material";
import { useMemo } from "react";
import { Link as LinkTo } from "react-router";
import { ObrasTopBar } from "../../components/obras_top_bar";
import { useSustentabilidadeViewModel } from "../../viewModels/obras_sustentabilidade_view_model";
import { GenerateResiduosColumnDefinition } from "./columns/residuo_columns";
import { GenerateEnergiaColumnDefinition } from "./columns/energia_columns";
import { ModalResiduo } from "./components/residuo_modal";
import { GenerateAguaColumnDefinition } from "./columns/agua_columns";
import { ModalEnergia } from "./components/energia_modal";
import { ModalAgua } from "./components/agua_modal";
import { ObrasReportModals } from "../../components/obras_report_modals";
import { ObrasTopHeader } from "../../components/obras_top_header";
import { Header } from "./components/header";
import { ControleTable } from "./components/controle_table";

import AlertError from "../../../../../common/components/alert_error";
import DeleteDialog from "../../../../../common/components/delete_dialog";
import CascateList from "../../../../../common/components/cascate_list";

export const Obras_sustentabilidade = () => {
  const viewModel = useSustentabilidadeViewModel();

  function formatNumber(value: number | null | undefined, minDecimals = 1, maxDecimals = 4): string {
    if (value == null) return "-";
    return value.toLocaleString("pt-BR", {
      minimumFractionDigits: minDecimals,
      maximumFractionDigits: maxDecimals,
    });
  }

  function formatMoney(value: number | null | undefined): string {
    if (value == null) return "-";
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  const columnsResiduos = useMemo(() => {
    return GenerateResiduosColumnDefinition({
      onActionEditar: viewModel.residuos.handleEditOpenState,
      onActionDeletar: viewModel.residuos.DeleteOpenModal,
    });
  }, [
    viewModel.residuos.handleEditOpenState,
    viewModel.residuos.DeleteOpenModal,
  ]);

  const columnsEnergia = useMemo(() => {
    return GenerateEnergiaColumnDefinition({
      onActionEditar: viewModel.energia.handleEditOpenState,
      onActionDeletar: viewModel.energia.DeleteOpenModal,
    });
  }, [
    viewModel.energia.handleEditOpenState,
    viewModel.energia.DeleteOpenModal,
  ]);

  const columnsAgua = useMemo(() => {
    return GenerateAguaColumnDefinition({
      onActionEditar: viewModel.agua.handleEditOpenState,
      onActionDeletar: viewModel.agua.DeleteOpenModal,
    });
  }, [
    viewModel.agua.handleEditOpenState,
    viewModel.agua.DeleteOpenModal,
  ]);

  return (
    <>
      <ObrasTopBar
        obrasReportState={viewModel.obrasReportState}
        state={viewModel.obrasTopHeaderState}
        selectedObra={viewModel.selectedObra ?? null}
        onBack={viewModel.handleBack}
      />

      <div className="flex w-full gap-2 flex-col py-2 px-4 mb-24">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            Obras
          </Link>
          <Link underline="hover" color="inherit" component="span">
            <LinkTo to="/Obras_lista">Lista de Obras</LinkTo>
          </Link>
          <Typography className="text-primary-green">Sustentabilidade</Typography>
        </Breadcrumbs>
        <div className="flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
          <ObrasTopHeader
            title="Sustentabilidade"
            selectedObra={viewModel.selectedObra ?? null}
            onReload={viewModel.handleReload}
          />
        </div>

        {viewModel.errorMessage !== undefined ? (
          <div className="w-full">
            <AlertError messageErro={viewModel.errorMessage} onTryAgain={viewModel.handleReload} />
          </div>
        ) : (
          <>
        {/* --- SEÇÃO DE RESÍDUOS --- */}
        <CascateList
          title="Controle de Resíduos"
          fontWeight='bold'
          defaultOpen={false}>
          <div className="w-full flex flex-col gap-4">
            <Header
              name="areaResiduo"
              area={viewModel.residuos.lista?.areaResiduo}
              loading={viewModel.residuos.loading}
              saving={viewModel.residuos.saving}
              onSave={viewModel.residuos.AtualizarArea}
              onNewItem={viewModel.residuos.abrirModalCreate}
            />

            {/* Tabela */}
            <ControleTable
              tableName="obras_sustentabilidade_residuos"
              rows={viewModel.residuos.lista?.pagedItens.items ?? []}
              columns={columnsResiduos}
              loading={viewModel.residuos.loading}
              getRowId={(row) => row.idControleResiduos}
              pagination={viewModel.residuos.pagination}
              onPageChange={viewModel.residuos.handlePageChange}
              onRowsPerPageChange={viewModel.residuos.handleRowsPerPageChange}
            />

            {/* Rodapé de totais */}
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 text-[11px] font-bold uppercase">
              <div>
                Média por Trabalhador:
                <span className="text-blue-600 block">{formatNumber(viewModel.residuos.lista?.mediaPorTrabalhador, 4, 4)}</span>
              </div>
              <div>
                Volume Total M³:
                <span className="text-blue-600 block">{formatNumber(viewModel.residuos.lista?.volumeTotalGeral, 1, 1)}</span>
              </div>
              <div>
                Valor Total:
                <span className="text-blue-600 block text-sm">{formatMoney(viewModel.residuos.lista?.valorTotalGeral)}</span>
              </div>
              <div>
                Média Mensal Acumulada:
                <span className="text-blue-600 block">{formatNumber(viewModel.residuos.lista?.mediaMensalAcumulada, 4, 4)}</span>
              </div>
              <div className="bg-orange-100 border border-orange-300 p-3 rounded">
                Média Geral Acumulada:
                <span className="text-blue-600 block font-semibold">
                  {formatNumber(viewModel.residuos.lista?.mediaGeralAcumulada, 4, 5) + "  (m³/m²)"}
                </span>
              </div>
            </div>
          </div>
        </CascateList>

        <Divider className="my-4" />

        <CascateList
          title="Controle de Energia"
          fontWeight='bold'
          defaultOpen={false}>
          <div className="w-full flex flex-col gap-4">
            <Header
              name="areaEnergia"
              area={viewModel.energia.lista?.areaEnergia}
              loading={viewModel.energia.loading}
              saving={viewModel.energia.saving}
              onSave={viewModel.energia.AtualizarArea}
              onNewItem={viewModel.energia.abrirModalCreate}
            />

            {/* Tabela */}

            <ControleTable
              tableName="obras_sustentabilidade_energia"
              rows={viewModel.energia.lista?.itens.items ?? []}
              columns={columnsEnergia}
              loading={viewModel.energia.loading}
              getRowId={(row) => row.idControleEnergia}
              pagination={viewModel.energia.pagination}
              onPageChange={viewModel.energia.handlePageChange}
              onRowsPerPageChange={viewModel.energia.handleRowsPerPageChange}
            />

            {/* Rodapé de totais */}
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 text-[11px] font-bold uppercase">
              <div>
                Média por Trabalhador:
                <span className="text-blue-600 block">{formatNumber(viewModel.energia.lista?.mediaPorTrabalhador, 4, 4)}</span>
              </div>
              <div>
                Consumo Total kWh:
                <span className="text-blue-600 block">{formatNumber(viewModel.energia.lista?.kwHTotalGeral, 1, 1)}</span>
              </div>
              <div>
                Valor Total:
                <span className="text-blue-600 block text-sm">{formatMoney(viewModel.energia.lista?.valorTotalGeral)}</span>
              </div>
              <div>
                Média Mensal Acumulada:
                <span className="text-blue-600 block">{formatNumber(viewModel.energia.lista?.mediaMensalAcumulada, 4, 4)}</span>
              </div>
              <div className="bg-orange-100 border border-orange-300 p-3 rounded">
                Média Geral Acumulada:
                <span className="text-blue-600 block">
                  {formatNumber(viewModel.energia.lista?.mediaGeralAcumulada, 4, 5) + "  (KWh/m²)"}
                </span>
              </div>
            </div>
          </div>
        </CascateList>

        <Divider className="my-4" />

        {/* --- SEÇÃO DE ÁGUA --- */}
        <CascateList
          title="Controle de Água"
          fontWeight='bold'
          defaultOpen={false}>
          <div className="w-full flex flex-col gap-4">
            <Header
              name="areaAgua"
              area={viewModel.agua.lista?.areaAgua}
              loading={viewModel.agua.loading}
              saving={viewModel.agua.saving}
              onSave={viewModel.agua.AtualizarArea}
              onNewItem={viewModel.agua.abrirModalCreate}
            />

            {/* Tabela */}
            <ControleTable
              tableName="obras_sustentabilidade_agua"
              rows={viewModel.agua.lista?.itens.items ?? []}
              columns={columnsAgua}
              loading={viewModel.agua.loading}
              getRowId={(row) => row.idControleAgua}
              pagination={viewModel.agua.pagination}
              onPageChange={viewModel.agua.handlePageChange}
              onRowsPerPageChange={viewModel.agua.handleRowsPerPageChange}
            />

            {/* Rodapé de totais */}
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 text-[11px] font-bold uppercase">
              <div>
                Média por Trabalhador:
                <span className="text-blue-600 block">{formatNumber(viewModel.agua.lista?.mediaPorTrabalhador, 4, 4)}</span>
              </div>

              <div>
                Volume Total M³:
                <span className="text-blue-600 block">{formatNumber(viewModel.agua.lista?.volumeTotalGeral, 1, 1)}</span>
              </div>

              <div>
                Valor Total:
                <span className="text-blue-600 block text-sm">{formatMoney(viewModel.agua.lista?.valorTotalGeral)}</span>
              </div>

              <div>
                Média Mensal Acumulada:
                <span className="text-blue-600 block">{formatNumber(viewModel.agua.lista?.mediaMensalAcumulada, 4, 4)}</span>
              </div>

              <div className="bg-orange-100 border border-orange-300 p-3 rounded">
                Média Geral Acumulada:
                <span className="text-blue-600 block">{formatNumber(viewModel.agua.lista?.mediaGeralAcumulada, 4, 5) + "  (m³/m²)"}</span>
              </div>
            </div>
          </div>
        </CascateList>
          </>
        )}
      </div>

      {/* Modais de Criação/Edição */}
      <ModalResiduo
        open={viewModel.residuos.abrirModal}
        saving={viewModel.residuos.saving}
        editmod={viewModel.residuos.editmod}
        dados={viewModel.residuos.dadosEditResiduo}
        onClose={viewModel.residuos.fecharModal}
        onDownload={viewModel.residuos.handleDownloadArquivo}
        onSubmit={viewModel.residuos.handleItemSubmit}

      />

      <ModalEnergia
        open={viewModel.energia.abrirModal}
        saving={viewModel.energia.saving}
        editmod={viewModel.energia.editmod}
        dados={viewModel.energia.dadosEdit}
        onClose={viewModel.energia.fecharModal}
        onSubmit={viewModel.energia.handleItemSubmit}
      />

      <ModalAgua
        open={viewModel.agua.abrirModal}
        saving={viewModel.agua.saving}
        editmod={viewModel.agua.editmod}
        dados={viewModel.agua.dadosEdit}
        onClose={viewModel.agua.fecharModal}
        onSubmit={viewModel.agua.handleItemSubmit}
      />

      <ObrasReportModals
        state={viewModel.obrasReportState}
      />

      {/* Dialog de Exclusão */}
      <DeleteDialog
        open={viewModel.residuos.openDeleteModal}
        onClose={() => viewModel.residuos.DeleteOpenModal(null, false)}
        onDelete={viewModel.residuos.handleDelete}
        title={"Deseja realmente excluir este registro de resíduo?"}
      />

      <DeleteDialog
        open={viewModel.energia.openDeleteModal}
        onClose={() => viewModel.energia.DeleteOpenModal(null, false)}
        onDelete={viewModel.energia.handleDelete}
        title={"Deseja realmente excluir este registro de energia?"}
      />

      <DeleteDialog
        open={viewModel.agua.openDeleteModal}
        onClose={() => viewModel.agua.DeleteOpenModal(null, false)}
        onDelete={viewModel.agua.handleDelete}
        title={"Deseja realmente excluir este registro de água?"}
      />
    </>
  );
};
